import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ControlButton } from "../styled";
import { IconVolume } from "../Icons/IconVolume";
import { useDispatch } from "react-redux";
import { setPlayerVolume } from "../../../redux/actions/actions";
import { useSetValidVolume } from "../../../hooks/useSetValidVolume";
import { volumeStepper } from "../../../utils/volumeStepper";
import { useDelayUnmount } from "../../../hooks/useDelayUnmount";
import {
  VolumeController,
  VolumeControllerWrapper,
  VolumeSlider,
  VolumeThumb,
  VolumeTrack,
  VolumeWrapper,
} from "./styled";

export const VolumeButton = ({ trackColor }) => {
  const dispatch = useDispatch();
  const [isSliderShow, setIsSliderShow] = useState(false);
  const [volume, setVolume] = useSetValidVolume();
  const shouldRenderChild = useDelayUnmount(isSliderShow, 300);

  const volumeWrapperRef = useRef(null); // обертка всего компонента
  const volumeSliderRef = useRef(null); // слайдер, включает в себя ползунок и полосу прогресса
  const thumbRef = useRef(null); // ползунок
  const preMuteVolume = useRef(50); // актуальное значение громкости звука перед mute
  const isDragging = useRef(false); // происходит ли dragging ползунка

  useEffect(() => {
    const dragging = (e) => {
      if (isDragging.current) {
        // отключаем userSelect, чтобы не было никаких лишних взаимодействий
        // с другими элементами во время dragging
        document.body.style.userSelect = "none";
        const sliderHeight = volumeSliderRef.current.clientHeight;

        // получаем координаты мыши относительно слайдера.
        const cursorPosRelativeToSlider =
          e.clientY - volumeSliderRef.current.getBoundingClientRect().y;

        // получаем координаты мыши относительно слайдера в процентах, отнимаем от 100, чтобы реверснуть проценты
        const cursorPosWhileGrabbingRelativeToSliderInPercents =
          100 - Math.ceil((cursorPosRelativeToSlider / sliderHeight) * 100);

        setVolume(cursorPosWhileGrabbingRelativeToSliderInPercents);
        setIsSliderShow(true);
      }
    };

    const stopDragging = (e) => {
      document.body.style.userSelect = "";
      isDragging.current = false;

      // проверка чтобы слайдер не закрывался, если отпустить курсор внутри слайдера.
      if (
        !volumeWrapperRef.current ||
        volumeWrapperRef.current.contains(e.target)
      ) {
        return;
      }

      setIsSliderShow(false);
    };

    window.addEventListener("mousemove", dragging);
    window.addEventListener("mouseup", stopDragging);

    return () => {
      window.removeEventListener("mousemove", dragging);
      window.removeEventListener("mouseup", stopDragging);
    };
  }, [setIsSliderShow, setVolume]);

  useEffect(() => {
    dispatch(setPlayerVolume(volume / 100));
  }, [dispatch, volume]);

  const controlVolumeOnSliderClick = (e) => {
    isDragging.current = true;

    const sliderHeight = volumeSliderRef.current.clientHeight;
    const sliderPositionFromTop =
      volumeSliderRef.current.getBoundingClientRect().y;

    // получаем позицию на месте клика в процентах и отнимаем от 100, чтобы реверснуть проценты.
    const clickedPosPercents =
      100 -
      Math.ceil(((e.clientY - sliderPositionFromTop) / sliderHeight) * 100);

    setVolume(clickedPosPercents);
  };

  const volumeRef = useRef(volume);
  volumeRef.current = volume;

  const controlVolumeWithWheel = useCallback(
    (e) => {
      e.preventDefault();
      // шаг увелечения громкости на колесо мыши.
      const step = 5;

      if (e.deltaY < 0 && volumeRef.current < 100) {
        setVolume(volumeStepper("up", step));
        return false;
      }

      if (e.deltaY > 0 && volumeRef.current > 0) {
        setVolume(volumeStepper("down", step));
        return false;
      }
    },
    [setVolume],
  );

  useLayoutEffect(() => {
    const wrapperRef = volumeWrapperRef.current;
    if (wrapperRef) {
      /**
       * Чтобы предотвратить скрол страницы при прокрутке колёсиком мышки
       * на ползунке громкости нужно добавлять слушатель с `passive: false`
       * чтобы вызвать `e.preventDefault()`, а в React это можно сделать
       * только через useRef
       */
      wrapperRef.addEventListener("wheel", controlVolumeWithWheel, {
        passive: false,
      });
    }

    return () => {
      if (wrapperRef) {
        wrapperRef.removeEventListener("wheel", controlVolumeWithWheel);
      }
    };
  }, [controlVolumeWithWheel]);

  const toggleMuteVolume = () => {
    if (volume === 0) {
      setVolume(preMuteVolume.current);
      return;
    }

    if (volume) {
      // сохраняем последнее значение громкости звука перед мутом
      preMuteVolume.current = volume;
      setVolume(0);
    }
  };

  const draggingStart = () => {
    isDragging.current = true;
  };

  const showSliderOnMouseEnter = () => {
    setIsSliderShow(true);
  };

  const hideSliderOnMouseLeave = () => {
    setIsSliderShow(false);
  };

  return (
    <VolumeWrapper
      ref={volumeWrapperRef}
      onMouseLeave={hideSliderOnMouseLeave}
      title="Громкость"
    >
      {shouldRenderChild && (
        <VolumeControllerWrapper forUnmountStyles={!isSliderShow}>
          <VolumeController>
            <VolumeSlider
              onMouseDown={controlVolumeOnSliderClick}
              ref={volumeSliderRef}
            >
              <VolumeThumb
                onMouseDown={draggingStart}
                ref={thumbRef}
                positionBottom={volume}
              />
              <VolumeTrack trackHeight={volume} trackBg={trackColor} />
            </VolumeSlider>
          </VolumeController>
        </VolumeControllerWrapper>
      )}

      <ControlButton
        onMouseEnter={showSliderOnMouseEnter}
        onClick={toggleMuteVolume}
      >
        <IconVolume volumePercents={volume} />
      </ControlButton>
    </VolumeWrapper>
  );
};
